import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import api from "../../helpers/api";
import {
  validateEventUrl,
  cleanUrl,
  getExpirationDate,
} from "../../helpers/processUrl";
import extractDate from "../../helpers/extractDate";
import { useAuth } from "../../contexts/Auth";
import { useUserEvents } from "../../contexts/UserEvent";

interface EventDialogProps {
  open: boolean;
  mode: "add" | "edit";
  initialEvent?: any; // event object for editing, undefined for add
  onClose: () => void;
  onSuccess: () => void;
}

function validateTicketmasterUrl(url: string): string | null {
  if (url.includes("ticketmaster") || url.includes("livenation")) {
    if (!(url.includes(".com") || url.includes(".ca"))) {
      return "Only .com or .ca domains for ticketmaster/livenation URLs are supported";
    }
    if (url.includes("ticketexchangebyticketmaster")) {
      return "Ticketexchange by ticketmaster is not yet suported";
    }
    if (url.includes("/artist/")) {
      return "Please enter a URL for a specific event";
    }
    if (url.includes("shop.ticketmaster")) {
      return "Please enter a https://ticketmaster.domain url";
    }
  }
  return null;
}

const EventDialog: React.FC<EventDialogProps> = ({
  open,
  mode,
  initialEvent,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { updateUrl } = useUserEvents();
  const [urlTracking, setUrlTracking] = React.useState<{
    url: string;
    quantity: number | undefined;
    maxPrice: string;
    sectionFilter: string[];
  }>({
    url: "",
    quantity: undefined,
    maxPrice: "",
    sectionFilter: [],
  });

  const [showDateInput, setShowDateInput] = React.useState(false);
  const [dateInput, setDateInput] = React.useState("");
  const [dateError, setDateError] = React.useState("");
  const [sectionInput, setSectionInput] = React.useState("");
  const [sectionError, setSectionError] = React.useState("");
  const [isStubhubUrl, setIsStubhubUrl] = React.useState(false);
  const [sectionHelperText, setSectionHelperText] = React.useState("");
  const [urlError, setUrlError] = React.useState<string>("");
  const [maxPriceError, setMaxPriceError] = React.useState<string>("");
  const [quantityError, setQuantityError] = React.useState<string>("");
  const [eventName, setEventName] = React.useState("");
  const [eventNameError, setEventNameError] = React.useState("");
  const hasActiveSubscription = user?.hasActiveSubscription;

  const parsedMaxPrice = parseFloat(urlTracking.maxPrice);
  const parsedQuantity = parseInt(String(urlTracking.quantity), 10);

  const totalPrice =
    !isNaN(parsedMaxPrice) && !isNaN(parsedQuantity)
      ? parsedMaxPrice * parsedQuantity
      : null;

  const getCurrencySymbol = () => {
    if (urlTracking.url.includes(".com")) return "$";
    if (urlTracking.url.includes(".co.uk")) return "£";
    if (urlTracking.url.includes(".ca")) return "C$";
    if (urlTracking.url.includes(".eu")) return "€";
    if (urlTracking.url.includes(".com.au")) return "AU$";
    return "$"; // Default to USD
  };

  useEffect(() => {
    if (mode === "edit" && initialEvent) {
      const url = initialEvent.trackedUrls?.[0]?.url;
      setEventName(initialEvent.name || "");
      setUrlTracking({
        url: url || "",
        quantity: initialEvent.trackedUrls?.[0]?.quantity || undefined,
        maxPrice: initialEvent.trackedUrls?.[0]?.maxPrice?.toString() || "",
        sectionFilter: initialEvent.trackedUrls?.[0]?.sectionFilter || [],
      });
      setSectionInput(
        (initialEvent.trackedUrls?.[0]?.sectionFilter || []).join(", ")
      );
      if (url.includes("stubhub")) {
        setSectionInput("");
        setIsStubhubUrl(true);
        setSectionHelperText(
          "To filter sections on Stubhub Events, on the event page select the zones you wish to filter for and copy the entire URL"
        );
      }
    } else if (mode === "add") {
      setEventName("");
      setUrlTracking({
        url: "",
        quantity: undefined,
        maxPrice: "",
        sectionFilter: [],
      });
      setSectionInput("");
    }
    setUrlError("");
    setMaxPriceError("");
    setQuantityError("");
    setSectionError("");
    setEventNameError("");
    setShowDateInput(false);
    setDateInput("");
    setDateError("");
  }, [mode, initialEvent, open]);

  const handleClose = () => {
    setUrlError("");
    setShowDateInput(false);
    setDateInput("");
    setDateError("");
    setUrlTracking({
      url: "",
      quantity: undefined,
      maxPrice: "",
      sectionFilter: [],
    });
    setSectionInput("");
    setSectionError("");
    setSectionHelperText("");
    setEventName("");
    setEventNameError("");
    setIsStubhubUrl(false);
  };

  const handleDialogClose = () => {
    handleClose();
    onClose();
  };

  const validateMaxPrice = (value: string) => {
    const maxPrice = Number(value.replace(" per ticket", ""));
    if (!value) return;
    if (isNaN(maxPrice) || maxPrice <= 0 || maxPrice > 100000) {
      setMaxPriceError("Please enter a valid price between 1 and 100000");
    } else {
      setMaxPriceError("");
    }
  };

  const validateQuantity = (value: string) => {
    const quantity = Number(value);
    if (!value) return;
    if (isNaN(quantity) || quantity <= 0 || quantity > 8) {
      setQuantityError("Please enter a valid quantity between 1 and 8");
    } else {
      setQuantityError("");
    }
  };

  const validateDate = (dateStr: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return "Date must be in YYYY-MM-DD format";
    }

    const date = new Date(dateStr);
    const today = new Date();
    today.setDate(today.getDate() - 1);
    const twoYearsFromNow = new Date();
    twoYearsFromNow.setFullYear(today.getFullYear() + 2);

    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    if (date < today) {
      return "Date cannot be in the past";
    }

    if (date > twoYearsFromNow) {
      return "Date cannot be more than 2 years in the future";
    }

    return "";
  };

  const validateSectionInput = (value: string) => {
    const sections = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const invalid = sections.find(
      (s) => s.length === 0 || /[^a-zA-Z0-9\s\-]/.test(s)
    );
    if (invalid) {
      return 'Sections should be comma-separated alphanumeric values (e.g., "101, 102A, VIP")';
    }
    return "";
  };

  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: "maxPrice" | "quantity"
  ) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    setUrlTracking({
      ...urlTracking,
      [field]: value,
    });

    if (field === "maxPrice") {
      validateMaxPrice(value);
    } else {
      validateQuantity(value);
    }
  };

  const handleUrlInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrlTracking({ ...urlTracking, url: value });
    setUrlError("");
    setIsStubhubUrl(false);
    setSectionHelperText("");
    if (value.includes("stubhub")) {
      setSectionInput("");
      setIsStubhubUrl(true);
      setSectionHelperText(
        "To filter sections on Stubhub Events, on the event page select the zones you wish to filter for and copy the entire URL"
      );
      if (value.includes("stubhub.prf.hn")) {
        setUrlError("Partner link detected - please enter direct Stubhub URL");
      }
    }
    const tmUrlError = validateTicketmasterUrl(value);
    if (tmUrlError) {
      setUrlError(tmUrlError);
    }
    if (!validateEventUrl(value)) {
      setUrlError(
        "Please enter a supported event URL from; Ticketmaster, SeatGeek, Stubhub, VividSeats or Tickpick"
      );
    }
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateInput(value);
    const error = validateDate(value);
    setDateError(error);
  };

  const handleSectionInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSectionInput(value);

    const sections = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const error = validateSectionInput(value);
    setSectionError(error);

    if (!error) {
      setUrlTracking((prev) => ({
        ...prev,
        sectionFilter: sections,
      }));
    }
  };

  const handleEventNameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEventName(value);
    if (!value.trim()) {
      setEventNameError("Event name is required");
    } else if (!/^[a-zA-Z0-9 ]+$/.test(value)) {
      setEventNameError("Event name must be alphanumeric characters only");
    } else if (value.length > 50) {
      setEventNameError("Event name must be under 50 characters");
    } else {
      setEventNameError("");
    }
  };

  const handleSubmit = async () => {
    setUrlError("");

    if (!eventName.trim()) {
      setEventNameError("Event name is required");
      return;
    }

    if (!urlTracking.url || !urlTracking.maxPrice || !urlTracking.quantity) {
      setUrlError(!urlTracking.url ? "URL is required" : "");
      setMaxPriceError(
        !urlTracking.maxPrice ? "Maximum price per ticket is required" : ""
      );
      setQuantityError(!urlTracking.quantity ? "Quantity is required" : "");
      return;
    }

    if (urlError || maxPriceError || quantityError || sectionError) {
      return;
    }

    const cleanedUrl = cleanUrl(urlTracking.url);
    const cleanedSections = sectionInput
      .split(",")
      .map((s) => s.replace(/section/gi, "").trim())
      .filter(Boolean);
    try {
      const extractedDate = await extractDate(cleanedUrl);
      if (extractedDate === "Shortened ticketmaster URL") {
        if (!showDateInput) {
          setShowDateInput(true);
          return;
        }
        if (!dateInput) {
          setDateError("Date is required for shortened URLs");
          return;
        }
        const dateError = validateDate(dateInput);
        if (dateError) {
          setDateError(dateError);
          return;
        }
      } else if (extractedDate === "Unable to find date in string") {
        setUrlError(
          "Unable to find date in string - avoid using URL shorteners"
        );
        return;
      }
      const expiresAt = getExpirationDate(
        showDateInput ? dateInput : extractedDate
      );

      if (mode === "edit") {
        await updateUrl(cleanedUrl, {
          maxPrice: Number(urlTracking.maxPrice),
          quantity: Number(urlTracking.quantity),
          sectionFilter: cleanedSections,
        });
      } else {
        await api.post("/user/events", {
          eventName: eventName.trim(),
          urls: [cleanedUrl],
          maxPrice: Number(urlTracking.maxPrice),
          quantity: Number(urlTracking.quantity),
          expiresAt,
          sectionFilter: cleanedSections,
        });
      }

      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.status === 409) {
        setEventNameError(error.response.data.error || "Event already exists");
      } else {
        const errorMessage =
          error.response?.data?.error || "Failed to add URL. Please try again.";
        setUrlError(errorMessage);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperProps={{
        sx: {
          width: { xs: "90%", sm: "80%", md: "60%" },
          "& .MuiFormHelperText-root.Mui-error": {
            color: "#FF3232",
          },
          "& .MuiFormLabel-root.Mui-error": {
            color: "#FF3232",
          },
          "& .MuiFormLabel-root": {
            color: "white",
          },
          "& .MuiDialogTitle-root": {
            padding: "16px 24px 8px",
          },
          "& .MuiDialogContent-root": {
            paddingTop: "20px",
          },
        },
      }}
    >
      <DialogTitle>
        {mode === "add" ? "Add New Event To Track" : "Edit Event"}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Event Name"
          placeholder="e.g. Taylor Swift - Detroit"
          type="text"
          fullWidth
          sx={{ mt: 2 }}
          value={eventName}
          onChange={handleEventNameInput}
          error={!!eventNameError}
          helperText={eventNameError}
          disabled={mode === "edit"}
          InputLabelProps={{
            sx: {
              top: "-6px",
              "&.Mui-focused": {
                top: "0px",
              },
              "&.MuiInputLabel-shrink": {
                top: "-4px",
                color: "white",
              },
            },
          }}
        />
        <TextField
          margin="dense"
          label="URL"
          type="url"
          fullWidth
          sx={{ mt: 2 }}
          value={urlTracking.url}
          onChange={handleUrlInput}
          error={!!urlError}
          helperText={urlError}
          disabled={mode === "edit"}
          InputLabelProps={{
            sx: {
              top: "-6px",
              "&.Mui-focused": {
                top: "0px",
              },
              "&.MuiInputLabel-shrink": {
                top: "-4px",
                color: "white",
              },
            },
          }}
        />
        {showDateInput && (
          <TextField
            margin="dense"
            label="Event Date (YYYY-MM-DD)"
            type="text"
            fullWidth
            value={dateInput}
            onChange={handleDateInput}
            error={!!dateError}
            helperText={dateError}
            sx={{ mt: 2 }}
            InputLabelProps={{
              sx: {
                top: "-6px",
                "&.Mui-focused": {
                  top: "0px",
                },
                "&.MuiInputLabel-shrink": {
                  top: "-4px",
                  color: "white",
                },
              },
            }}
          />
        )}
        {hasActiveSubscription ? (
          <TextField
            margin="dense"
            label="Filter sections (comma-separated values e.g., 101, 102A, VIP)"
            type="text"
            inputMode="text"
            fullWidth
            value={sectionInput}
            placeholder="101, 102, GA, Floor 2, VIP"
            sx={{ mt: 3 }}
            onChange={handleSectionInput}
            error={!!sectionError}
            helperText={
              isStubhubUrl ? (
                <Typography variant="caption" sx={{ color: "white" }}>
                  {sectionHelperText}
                </Typography>
              ) : (
                sectionError
              )
            }
            disabled={isStubhubUrl}
            InputLabelProps={{
              sx: {
                top: "-6px",
                "&.Mui-focused": {
                  top: "0px",
                },
                "&.MuiInputLabel-shrink": {
                  top: "-4px",
                  color: "white",
                },
              },
            }}
            FormHelperTextProps={{
              sx: {
                color: "white",
              },
            }}
          />
        ) : (
          <TextField
            margin="dense"
            label="Filter sections (comma-separated values e.g., 101, 102A, VIP)"
            type="text"
            inputMode="text"
            fullWidth
            sx={{ mt: 3 }}
            helperText={
              <Typography variant="caption" sx={{ color: "white" }}>
                Section filtering is only available to Pro members.{" "}
                <a
                  href="/plans"
                  style={{ color: "#90caf9", textDecoration: "underline" }}
                >
                  Upgrade
                </a>
              </Typography>
            }
            disabled={true}
            InputLabelProps={{
              sx: {
                top: "-6px",
                "&.Mui-focused": {
                  top: "0px",
                },
                "&.MuiInputLabel-shrink": {
                  top: "-4px",
                  color: "white",
                },
              },
            }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <TextField
            margin="dense"
            label="Max Per Ticket (including fees)"
            type="text"
            inputMode="numeric"
            fullWidth
            value={urlTracking.maxPrice}
            sx={{ mt: 3 }}
            onChange={(e) => handleNumberInput(e, "maxPrice")}
            error={!!maxPriceError}
            helperText={maxPriceError}
            InputProps={{
              endAdornment: urlTracking.maxPrice ? (
                <InputAdornment position="end">per ticket</InputAdornment>
              ) : null,
            }}
            InputLabelProps={{
              sx: {
                top: "-6px",
                "&.Mui-focused": {
                  top: "0px",
                },
                "&.MuiInputLabel-shrink": {
                  top: "-4px",
                  color: "white",
                },
              },
            }}
          />
          <Box sx={{ flex: 1, minWidth: { sm: "35%" } }}>
            <Select
              margin="dense"
              label="Quantity"
              fullWidth
              value={urlTracking.quantity || ""}
              sx={{
                mt: 3,
                height: "41px",
              }}
              onChange={(e) => {
                const value = parseInt(String(e.target.value), 10) || undefined;
                setUrlTracking({
                  ...urlTracking,
                  quantity: value,
                });
                if (value && value > 0 && value <= 8) {
                  setQuantityError("");
                }
              }}
              error={!!quantityError}
              displayEmpty
              inputProps={{
                "aria-label": "Quantity",
              }}
            >
              <MenuItem value="" disabled>
                Quantity
              </MenuItem>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
            {quantityError && (
              <Typography
                variant="caption"
                sx={{
                  pl: 1.8,
                  color: "#FF3232",
                  mt: 0.5,
                  display: "block",
                }}
              >
                {quantityError}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <Typography sx={{ ml: 4, fontWeight: "bold", fontSize: 18 }}>
        Total Order Value: {getCurrencySymbol()}
        {totalPrice?.toLocaleString()}
      </Typography>
      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        <Button
          disabled={
            !!urlError || !!sectionError || !!quantityError || !!maxPriceError
          }
          onClick={handleSubmit}
        >
          {mode === "add" ? "Add" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventDialog;
